import React, { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import CustomerNav from "@/components/customer/CustomerNav";
import MyTrips from "@/components/customer/MyTrips";
import MyProfile from "@/components/customer/MyProfile";
import MyCards from "@/components/customer/MyCards";
import { DriverAppBanners } from "@/components/DriverAppBanners";

const TABS = {
  trips: { title: "My Trips", sub: "Your upcoming and past rides — all in one place." },
  profile: { title: "My Profile", sub: "Keep your contact details current so chauffeurs can reach you." },
  cards: { title: "My Cards", sub: "Payment methods on file for faster checkout." },
};

export default function Customer() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const { tab = "trips" } = useParams();
  const [trips, setTrips] = useState([]);
  const [busy, setBusy] = useState(true);

  const loadTrips = useCallback(() => api.get("/bookings/me").then(r => setTrips(r.data)).catch(() => {}), []);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login", { replace: true }); return; }
    loadTrips().finally(() => setBusy(false));
    const id = setInterval(loadTrips, 30000); // live status refresh
    return () => clearInterval(id);
  }, [user, loading, nav, loadTrips]);

  if (!user) return null;
  if (!TABS[tab]) return <Navigate to="/customer/trips" replace/>;
  const meta = TABS[tab];

  return (
    <div data-testid="customer-dashboard" className="public-light bg-[#050505] min-h-screen text-[#F5F2EB] pb-24 md:pb-0">
      <Navbar/>
      <section className="pt-28 md:pt-32 pb-12 px-5 md:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="overline mb-3">My account</div>
          <h1 className="font-[Outfit] text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-light mb-2 flex items-center gap-4">
            {user.picture && <img src={user.picture} alt="" className="w-12 h-12 md:w-14 md:h-14 rounded-full object-cover border border-[#D4AF37]/40" data-testid="greeting-avatar"/>}
            <span>Hello, <span className="italic font-normal">{user.name?.split(" ")[0]}.</span></span>
          </h1>
          <p className="text-[#A1A1AA] mb-8" data-testid="customer-tab-subtitle">{meta.sub}</p>

          <CustomerNav/>

          {(user.role === "dispatcher" || user.role === "driver") && (
            <div className="flex flex-wrap gap-3 mb-8">
              {user.role === "dispatcher" && <button className="btn-ghost" onClick={() => nav("/dispatcher")}>Go to Dispatcher</button>}
              {user.role === "driver" && <button className="btn-ghost" onClick={() => nav("/driver")}>Go to Driver view</button>}
            </div>
          )}

          <h2 className="sr-only">{meta.title}</h2>
          <div data-testid={`customer-tab-panel-${tab}`}>
            {tab === "trips" && <MyTrips trips={trips} busy={busy} onChanged={loadTrips}/>}
            {tab === "profile" && <MyProfile/>}
            {tab === "cards" && <MyCards/>}
          </div>

          {tab === "trips" && (
            <div className="-mx-5 mt-12">
              <DriverAppBanners
                installTitle="Install the Delux Limo app"
                installBody="One tap adds Delux Limo to your home screen — book, track and get ride alerts like a real app."
                pushTitle="Get ride alerts"
                pushBody="We'll notify you when your chauffeur is assigned, on the way, and at your pickup."
                pushSuccess="Ride alerts on — we'll ping you when your chauffeur is en route"
                storagePrefix="cust"
              />
            </div>
          )}
        </div>
      </section>
      <Footer/>
    </div>
  );
}
